import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeader from '@/components/feature/PageHeader';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import CultureCard from '@/components/feature/CultureCard';
import ShareBar from '@/components/feature/ShareBar';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useHashScroll } from '@/hooks/useHashScroll';
import { cultureItems } from '@/mocks/culture';
import { formatDate } from '@/lib/content';

const PAGE_SIZE = 4;

export default function CultureCategoryPage() {
  usePageMeta({
    title: '动漫文化 - 琉璃神社',
    description:
      '琉璃神社动漫文化专题，从物哀美学、赛博朋克到分镜语言与同人生态，提供图文结合的深度文化解读与分享。',
    keywords: '动漫文化,文化专题,二次元文化,物哀美学,分镜研究,同人文化,琉璃神社',
  });
  useHashScroll();

  const [page, setPage] = useState(1);
  const [featured] = cultureItems;
  const rest = cultureItems.slice(1);
  const totalPages = Math.max(1, Math.ceil(rest.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const shown = rest.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const changePage = (next: number) => {
    setPage(next);
    document.getElementById('topics')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <SiteLayout>
      <PageHeader
        eyebrow="Culture"
        title="动漫文化"
        description="动漫文化深度解读及分享。我们尝试把作品放回它的文化语境里——美学、技术、产业与社群，都值得慢慢读。"
        breadcrumb={[{ label: '动漫文化' }]}
      />

      <div className="shell py-9 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          <Link
            to={`/detail/culture/${featured.id}`}
            className="group lg:col-span-8 relative rounded-lg overflow-hidden aspect-[16/10] lg:aspect-[16/9] bg-primary-950 cursor-pointer"
          >
            <img
              src={featured.cover}
              alt={featured.title}
              title={`${featured.title} - 琉璃神社文化专题`}
              className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/35 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded bg-accent-500 text-primary-950 text-[11px] leading-none">
                  本期主推
                </span>
                <span className="text-[11px] text-background-100/70 font-label">
                  {formatDate(featured.date)} · {featured.readTime}
                </span>
              </div>
              <h2 className="mt-3 text-lg md:text-3xl font-semibold text-background-50 leading-8 md:leading-10">
                {featured.title}
              </h2>
              <p className="mt-1.5 text-xs md:text-sm text-accent-300 tracking-wide">{featured.subtitle}</p>
              <p className="mt-3 text-xs md:text-sm text-background-100/75 leading-6 max-w-2xl clamp-2">
                {featured.summary}
              </p>
            </div>
          </Link>

          <div className="lg:col-span-4 rounded-lg border border-background-200/80 bg-background-100/60 p-5 md:p-6 flex flex-col">
            <p className="text-[11px] tracking-[0.2em] uppercase text-accent-600 font-label">编辑手记</p>
            <p className="mt-3 text-sm text-foreground-700 leading-7">
              文化专题不做资讯式的速读。每篇都在 3,000 字以上，配有作者手记与延伸阅读，适合在一个安静的晚上慢慢看。
            </p>
            <div className="mt-5 space-y-3">
              {[
                { icon: 'ri-quill-pen-line', label: '长文深度解读' },
                { icon: 'ri-image-line', label: '图文结合的排版' },
                { icon: 'ri-bookmark-line', label: '可分享的独立页面' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5 text-xs text-secondary-600">
                  <span className="w-7 h-7 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-primary-600">
                    <i className={`${item.icon} text-sm`}></i>
                  </span>
                  {item.label}
                </div>
              ))}
            </div>
            <div className="mt-auto pt-5">
              <ShareBar title={featured.title} />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full bg-background-100/70 border-y border-background-200/70 py-10 md:py-14">
        <div id="topics" className="shell scroll-mt-24">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-px bg-accent-500"></span>
                <span className="text-[11px] tracking-[0.24em] uppercase text-accent-600 font-label">
                  All Topics
                </span>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold">全部文化专题</h2>
            </div>
            <span className="text-xs text-secondary-500 whitespace-nowrap">
              共 <span className="text-foreground-900 font-label">{cultureItems.length}</span> 个专题
            </span>
          </div>

          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                {shown.map((item) => (
                  <CultureCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination
                total={rest.length}
                pageSize={PAGE_SIZE}
                page={current}
                onChange={changePage}
              />
            </>
          ) : (
            <EmptyState
              icon="ri-quill-pen-line"
              title="专题内容正在整理中"
              description="编辑团队正在准备新的文化专题，先去看看动漫资讯或作品档案吧。"
              actionLabel="浏览动漫资讯"
              actionTo="/category/news"
            />
          )}
        </div>
      </div>
    </SiteLayout>
  );
}